import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, ChevronRight, Lock, Loader2, UserCog } from 'lucide-react';
import { adminApi } from '../api/admin';
import { getErrorMessage } from '../api/client';
import { useAdminAuthStore } from '../store/adminAuthStore';

function SettingsPage() {
  const {
    adminUser,
    syncProfile,
    updateProfile,
    clearError,
    error: storeError,
    isLoading,
  } = useAdminAuthStore();

  const [firstName, setFirstName] = useState(adminUser?.firstName ?? '');
  const [lastName, setLastName] = useState(adminUser?.lastName ?? '');
  const [phoneNumber, setPhoneNumber] = useState(adminUser?.phoneNumber ?? '');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMessage, setPwMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [twoFactorQr, setTwoFactorQr] = useState('');
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorBusy, setTwoFactorBusy] = useState(false);
  const [twoFactorLocalError, setTwoFactorLocalError] = useState('');

  useEffect(() => {
    void syncProfile();
  }, [syncProfile]);

  useEffect(() => {
    if (!adminUser) return;
    setFirstName(adminUser.firstName ?? '');
    setLastName(adminUser.lastName ?? '');
    setPhoneNumber(adminUser.phoneNumber ?? '');
  }, [adminUser]);

  const mfaIncomplete = !!(adminUser && adminUser.twoFactorEnabled === false);

  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await updateProfile({ firstName, lastName, phoneNumber });
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMessage(null);
    if (newPassword !== confirmPassword) {
      setPwMessage({ type: 'err', text: 'New passwords do not match.' });
      return;
    }
    setPwSaving(true);
    try {
      await adminApi.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword: confirmPassword,
      });
      setPwMessage({ type: 'ok', text: 'Password updated.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwMessage({ type: 'err', text: getErrorMessage(err, 'Could not update password.') });
    } finally {
      setPwSaving(false);
    }
  };

  const startAuthenticatorSetup = async () => {
    setTwoFactorLocalError('');
    setTwoFactorBusy(true);
    try {
      const res = await adminApi.setupTwoFactor();
      setTwoFactorQr(res.data.qrCodeUri);
      setTwoFactorSecret(res.data.secret);
      setTwoFactorCode('');
    } catch (err) {
      setTwoFactorLocalError(getErrorMessage(err, 'Could not start 2FA setup.'));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const enableAuthenticator = async (e: React.FormEvent) => {
    e.preventDefault();
    if (twoFactorCode.length !== 6) return;
    setTwoFactorLocalError('');
    setTwoFactorBusy(true);
    try {
      await adminApi.enableTwoFactor(twoFactorCode);
      await syncProfile();
      setTwoFactorQr('');
      setTwoFactorSecret('');
      setTwoFactorCode('');
    } catch (err) {
      setTwoFactorLocalError(getErrorMessage(err, 'Invalid code or server error.'));
    } finally {
      setTwoFactorBusy(false);
    }
  };

  const qrImg = twoFactorQr
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFactorQr)}`
    : '';

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="text-gray-600 text-sm mt-1">Admin profile and security</p>
      </div>

      {mfaIncomplete && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex gap-3">
          <AlertCircle className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">Two-factor authentication required</p>
            <p className="text-sm text-amber-800 mt-1">
              Until you enable 2FA, the rest of the admin app is unavailable. Finish setup with your authenticator
              app below to unlock all sections.
            </p>
          </div>
        </div>
      )}

      {adminUser?.twoFactorEnabled === true && (
        <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 flex gap-2 items-start">
          <Check className="h-5 w-5 text-green-700 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-900">
            Two-factor authentication is on. Turning it off is not permitted for administrators.
          </p>
        </div>
      )}

      {!mfaIncomplete && (
        <Link
          to="/settings/team"
          className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm hover:border-indigo-200 hover:bg-indigo-50/40 transition-colors"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <UserCog size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900">Team</p>
            <p className="text-sm text-gray-500">Invite staff to the admin panel</p>
          </div>
          <ChevronRight size={18} className="text-gray-400" />
        </Link>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
        <form onSubmit={handleProfileSave} className="space-y-4 max-w-lg">
          {storeError && (
            <p className="text-sm text-red-600">{storeError}</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Phone number</label>
            <input
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm max-w-lg"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            {isLoading ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </section>

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1 flex items-center gap-2">
          <Lock className="w-5 h-5 text-gray-500" /> Change password
        </h2>
        <form onSubmit={handlePasswordSubmit} className="space-y-4 max-w-lg mt-4">
          {pwMessage && (
            <p className={`text-sm ${pwMessage.type === 'ok' ? 'text-green-700' : 'text-red-600'}`}>{pwMessage.text}</p>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Current password</label>
            <input
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">New password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              required
            />
          </div>
          <button
            type="submit"
            disabled={pwSaving}
            className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {pwSaving ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </section>

      {mfaIncomplete && (
        <section className="bg-white rounded-xl border border-indigo-100 shadow-sm p-6 ring-2 ring-indigo-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authenticator app (2FA)</h2>
          <p className="text-sm text-gray-600 mb-4">
            Scan the QR code in your app, then enter the one-time verification code below.
          </p>

          {!twoFactorQr ? (
            <button
              type="button"
              onClick={() => void startAuthenticatorSetup()}
              disabled={twoFactorBusy}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {twoFactorBusy && <Loader2 className="animate-spin h-4 w-4" />}
              Generate QR code
            </button>
          ) : (
            <form onSubmit={enableAuthenticator} className="space-y-4 max-w-md">
              {qrImg && (
                <div className="flex justify-start">
                  <img src={qrImg} alt="2FA QR" className="rounded-lg border border-gray-100" />
                </div>
              )}
              <p className="text-xs font-mono break-all bg-gray-50 rounded p-2 border">{twoFactorSecret}</p>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Verification code</label>
                <input
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-40 rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono tracking-widest"
                  placeholder="000000"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={twoFactorBusy || twoFactorCode.length !== 6}
                  className="bg-green-600 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  Enable 2FA
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTwoFactorQr('');
                    setTwoFactorSecret('');
                    setTwoFactorCode('');
                  }}
                  className="text-sm text-gray-600 underline"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {twoFactorLocalError && (
            <p className="text-sm text-red-600 mt-3">{twoFactorLocalError}</p>
          )}
        </section>
      )}
    </div>
  );
}

export default SettingsPage;
